import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth/auth-context";
import { recordTermsAcceptance } from "../lib/terms/acceptance";
import { TERMS_VERSION } from "../lib/terms/version";
import { AppHeader } from "./AppHeader";
import { PageShell } from "./PageShell";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";

interface TermsGateProps {
  /** Appelé une fois l'acceptation enregistrée avec succès. */
  onAccepted: () => void;
}

type GateStatus = "idle" | "pending" | "error";

// Écran bloquant plein écran (premier login, ou nouvelle version des
// conditions) : rendu par ProtectedRoute tant que TERMS_VERSION n'a pas de
// ligne terms_acceptances pour l'utilisateur courant. « Se déconnecter » évite
// de piéger l'usager qui refuse — c'est une porte de sortie, pas un mur sans issue.
export function TermsGate({ onAccepted }: TermsGateProps) {
  const { user, client, signOut } = useAuth();
  const [status, setStatus] = useState<GateStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleAccept() {
    if (!user) return;
    setStatus("pending");
    setErrorMessage(null);

    const result = await recordTermsAcceptance(client, user.id, TERMS_VERSION);
    if (result.ok) {
      onAccepted();
    } else {
      setStatus("error");
      setErrorMessage(result.error);
    }
  }

  return (
    <PageShell width="narrow" center>
      {/* Hero — cohérent avec l'écran de connexion */}
      <AppHeader
        variant="plain"
        title="Avant de continuer"
        subtitle="Pour utiliser Écoute, veuillez accepter les Conditions d'utilisation et prendre connaissance de la page Vos données."
      />

      <nav className="flex flex-col gap-1">
        <Link
          to="/conditions"
          className="text-small text-flambeau-700 underline decoration-dotted underline-offset-4 hover:text-flambeau-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange"
        >
          Conditions d'utilisation
        </Link>
        <Link
          to="/confidentialite"
          className="text-small text-flambeau-700 underline decoration-dotted underline-offset-4 hover:text-flambeau-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange"
        >
          Vos données
        </Link>
      </nav>

      {status === "error" && (
        <Card variant="danger">
          <p role="alert" className="m-0">
            {errorMessage}
          </p>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        <Button
          type="button"
          variant="primary"
          size="md"
          block
          disabled={status === "pending"}
          onClick={handleAccept}
        >
          {status === "pending" ? "Un instant…" : "J'accepte et continuer"}
        </Button>
        <button
          type="button"
          onClick={() => signOut()}
          className="self-start text-small text-muted underline decoration-dotted underline-offset-4 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-orange"
        >
          Se déconnecter
        </button>
      </div>
    </PageShell>
  );
}
